import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from '@/components/AdCard/AdCard.styles';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import {
  HeartActiveIcon,
  HeartIconInactive,
} from '@/components/UI/Icons/Icons';
import { formatNumber, getAdLabelKey } from '@/utils/helpers';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { getMessageTime } from '@/utils/datetime.utils';
import { ROUTES } from '@/constants/routes';
import NoSsr from '@/components/NoSsr/NoSsr';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { Ad } from '@/api/types/common.types';
import { KindIcons } from '@/components/KindIcons/KindIcons';
import { useIsShop } from '@/hooks/useIsShop';
import { useBookmark } from '@/hooks/useBookmark';
import { PhotoOverlay } from '@/components/PhotoOverlay';
import { EDIT_AD_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import { useUserFavorites } from '@/features/BookmarkCollections';
import { AdTransitionSource } from '@/api/types/ads.types';
import { getAdTransitionSourceCookie } from '@/utils/cookies';
import { CommonTestIds } from '@/__e2e__/testIds/common';

interface AdCardProps {
  ad?: Ad;
  isLoading?: boolean;
  className?: string;
  hasActions?: boolean;
  hasFavorite?: boolean;
  onTriggerRemove?: (id: Ad['id']) => void;
  priority?: boolean;
  overlayText?: string;
  onAddToFavorites?: (ad: Ad) => void;
  onRemoveFromFavorites?: (ad: Ad) => void;
  adTransitionSource?: AdTransitionSource;
}

export const AdCard: React.FC<AdCardProps> = ({
  ad,
  isLoading,
  className,
  hasActions = false,
  hasFavorite = true,
  onTriggerRemove,
  onAddToFavorites,
  onRemoveFromFavorites,
  priority,
  overlayText,
  adTransitionSource,
}) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useRouter();
  const customPush = useCustomPush();
  const { isBookmarked: isFavorite, toggleBookmark } = useBookmark({
    ad,
  });
  const isShop = useIsShop();
  const { refetch } = useUserFavorites({ skip: true });

  const isBookmarksPage = pathname === ROUTES.bookmarks;
  const cardLegacyId = ad?.legacyResourceId
    ? String(ad.legacyResourceId)
    : undefined;

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!ad) return;

    await toggleBookmark();

    if (!isFavorite) {
      await refetch();
      onAddToFavorites?.(ad);
    } else {
      onRemoveFromFavorites?.(ad);
    }
  };

  function onTriggerRemoveClick(e: React.MouseEvent, id: Ad['id']) {
    e.preventDefault();
    onTriggerRemove?.(id);
  }

  function onEditClick(e: React.MouseEvent) {
    e.preventDefault();
    if (!ad) return;

    customPush(`${ad.path}/${EDIT_AD_PAGE_SLUG}`);
  }

  function trackAdTransitionSourceCookie() {
    if (adTransitionSource && cardLegacyId) {
      getAdTransitionSourceCookie(cardLegacyId).set(adTransitionSource);
    }
  }

  function onCardAuxClick(e: React.MouseEvent) {
    if (e.button !== 1) return;

    trackAdTransitionSourceCookie();
  }

  if (!ad || isLoading) {
    return (
      <S.Box
        $hasActions={hasActions}
        className={className}
        data-testid="ad-card-skeleton-loader"
      >
        <SkeletonLoader borderRadius={0} width="100%" height="130px" />
        <S.TextsBox>
          <SkeletonLoader width="74px" />
          <S.SkeletonTitle />
          <SkeletonLoader width="113px" />
        </S.TextsBox>
      </S.Box>
    );
  }

  const labelKey = getAdLabelKey(ad.features);
  const labelText = labelKey ? t(labelKey) : null;

  return (
    <S.Box
      dataTestId={CommonTestIds.adCards.card}
      data-stat={!isBookmarksPage ? 'ad-card-link' : undefined}
      as={CustomLink}
      href={ad.path}
      className={className}
      $hasActions={hasActions}
      onClick={trackAdTransitionSourceCookie}
      onAuxClick={onCardAuxClick}
      onTouchStart={trackAdTransitionSourceCookie}
      onContextMenu={trackAdTransitionSourceCookie}
    >
      <S.ImgBox>
        <img
          src={ad.photo?.url}
          alt={ad.title}
          {...(priority ? { fetchPriority: 'high' } : { loading: 'lazy' })}
        />
        {!!overlayText && (
          <PhotoOverlay
            dataTestId={CommonTestIds.adCards.photoOverlay}
            overlayText={overlayText}
            fontSize={13}
            paddingTop={14}
          />
        )}
        {hasFavorite && (
          <S.IsFavorite
            data-testid={CommonTestIds.adCards.bookmarkButton}
            appearance="transparent"
            aria-label="Toggle bookmark"
            hasPadding={false}
            onClick={toggleFavorite}
            onTouchStart={e => e.stopPropagation()}
            data-stat={
              !isFavorite && isShop && ad?.shop
                ? 'shop-ad-bookmarking'
                : undefined
            }
            className="products-bookmarking"
          >
            {isFavorite ? (
              <HeartActiveIcon
                data-testid={CommonTestIds.adCards.isBookmarked}
              />
            ) : (
              <HeartIconInactive
                data-testid={CommonTestIds.adCards.isNotBookmarked}
                className="add_bookmark bookmark__link"
              />
            )}
          </S.IsFavorite>
        )}

        {labelText ? <S.Label>{labelText}</S.Label> : null}

        <KindIcons kinds={ad.kinds} isInCard />
      </S.ImgBox>
      <S.TextsBox>
        {ad.price !== null && (
          <Typography.Numbers
            $variant="2"
            data-testid={CommonTestIds.adCards.price}
          >
            {formatNumber(ad.price, 2, true)} AZN
          </Typography.Numbers>
        )}

        <S.Title
          $variant="2"
          $hasActions={hasActions}
          data-testid={CommonTestIds.adCards.title}
        >
          {ad.title}
        </S.Title>

        <Typography.Caption
          data-testid={CommonTestIds.adCards.additional}
          $isEllipsis
        >
          {ad.region},{' '}
          <NoSsr>{getMessageTime(new Date(ad.updatedAt), i18n, true)}</NoSsr>
        </Typography.Caption>

        {hasActions && (
          <>
            <S.Action $isLink onClick={onEditClick}>
              {t('common.edit')}
            </S.Action>
            <Typography.Caption
              $isLink
              onClick={e => onTriggerRemoveClick(e, ad.id)}
            >
              {t('common.remove')}
            </Typography.Caption>
          </>
        )}
      </S.TextsBox>
    </S.Box>
  );
};
